import {
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Tooltip,
  Typography,
  ListItemIcon,
  ListItemText,
  Theme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ContentCopyIcon from 'packages/lib/assets/icons/content_copy-24px.svg?react';
import { useState, useRef, FC, useCallback } from 'react';
import clsx from 'clsx';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    marginRight: theme.spacing(0.5),
    position: 'relative',
  },
  typography: {
    display: 'flex',
  },
}));

export interface CopyTokenProps {
  copyText: string;
  ui?: 'button' | 'typography' | 'list' | 'profilemenu';
  label?: string;
  iconSize?: string;
  offsetY?: number;
}
export const CopyToken: FC<React.PropsWithChildren<CopyTokenProps>> = ({
  copyText,
  ui,
  label,
  iconSize = undefined,
  offsetY,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const copyIconStyles = useStyles().classes;

  const handleCopy = useCallback((): void => {
    window.navigator.clipboard.writeText(copyText).then(
      () => setShowSnackBar(true),
      () => {
        setShowManualDialog(true);
        setTimeout(() => {
          textAreaRef.current?.select();
        }, 100);
      }
    );
  }, [copyText]);

  const handleRequestClose = useCallback((): void => {
    setShowSnackBar(false);
  }, []);
  if (!copyText) return null;

  // Not quite sure if this is the proper direction for this component,
  // but with the switch statement, we get some added flexibility!
  // Probably needs cleanup/TLC later on, but for now this is pretty handy.
  let content;

  switch (ui) {
    case 'button':
      content = (
        <Button variant="contained" color="primary" onClick={handleCopy}>
          Copy {label || 'Access Token'}
        </Button>
      );
      break;
    case 'typography':
      content = (
        <Typography onClick={handleCopy} className={copyIconStyles.typography}>
          <ContentCopyIcon
            style={{ height: iconSize ? iconSize : undefined }}
            className={clsx('cursor-pointer', copyIconStyles.root)}
          />
          Copy {label || 'Access Token'}
        </Typography>
      );
      break;
    case 'profilemenu':
      content = (
        <Typography
          onClick={handleCopy}
          className={copyIconStyles.typography}
          style={{ alignItems: 'center', fontSize: '13px' }}
        >
          <ContentCopyIcon
            fill="white"
            className={clsx('cursor-pointer', copyIconStyles.root)}
            style={{ width: '13px', height: '13px' }}
          />
          Copy {label || 'Access Token'}
        </Typography>
      );
      break;
    case 'list':
      content = (
        <>
          Yo
          <ListItemIcon onClick={handleCopy}>
            <ContentCopyIcon />
          </ListItemIcon>
          <ListItemText
            onClick={handleCopy}
            data-testid="copy-access-token-menu-item"
            primary={`Copy ${label || 'Access Token'}`}
          />
        </>
      );
      break;
    default:
      content = (
        <>
          <ContentCopyIcon
            onClick={handleCopy}
            className={clsx('cursor-pointer', copyIconStyles.root)}
            style={{ color: 'red' }}
          />
        </>
      );
  }

  return (
    <>
      <Tooltip placement="top" title={`Copy ${label || 'token'} to clipboard`}>
        {content}
      </Tooltip>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackBar}
        onClose={handleRequestClose}
        message={<span>{label || 'Token'} has been copied to clipboard!</span>}
        autoHideDuration={3000}
      />
      <Dialog
        open={showManualDialog}
        onClose={(): void => setShowManualDialog(false)}
      >
        <DialogContent>
          We failed to copy it silently, please copy it manually here:
          <textarea
            ref={textAreaRef}
            value={copyText}
            readOnly
            rows={Math.ceil(copyText.length / 55)}
            cols={65}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CopyToken;
